import { useCallback } from "react";
import { parseUnits } from '@ethersproject/units';
import { CurrencyAmount, Currency } from "@uniswap/sdk-core";
import JSBI from 'jsbi';
import { 
  DefaultTxnSettings,
  typeInput, 
  selectLeverage, 
  selectPositionSide, 
  setSlippage,
  setTxnDeadline,
  resetBuildState } from "./actions";
import { AppState } from "../state";
import { useActiveWeb3React } from "../../hooks/web3";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useQueryPositionQuery as usePositionListQuery, useLiquidatedPositionsQuery, useNumberOfPositionsQuery, useOpenPositionsQuery, useMarketOpenPositionsQuery, useUnwindsQuery, OpenPositionsQuery, UnwindsQuery, LiquidatedPositionsQuery, MarketOpenPositionsQuery } from "../data/generated";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { PositionStatus } from "../../pages/Positions/PositionsPage";

export function useBuildState(): AppState['build'] {
  return useAppSelector((state) => state.build);
}

const slippageRegex: RegExp = /^(?:\d{1,2}(?:\.\d{0,2})?|\.\d{1,2}|100(?:\.0{1,2})?)?$/;

export function useBuildActionHandlers(): {
  onAmountInput: (typedValue: string | undefined) => void;
  onSelectLeverage: (selectedLeverage: string) => void;
  onSelectPositionSide: (isLong: boolean) => void;
  onSetSlippage: (setSlippageValue: DefaultTxnSettings | string) => void;
  onSetTxnDeadline: ( txnDeadline: DefaultTxnSettings | string) => void;
  onResetBuildState: () => void;
} {
  const dispatch = useAppDispatch();

  const onAmountInput = useCallback(
    (typedValue: string | undefined) => {
      dispatch(typeInput({ typedValue }))
    },
    [dispatch]
  );

  const onSelectLeverage = useCallback(
    (selectedLeverage: string) => {
      dispatch(selectLeverage({ selectedLeverage }))
    },
    [dispatch]
  );

  const onSelectPositionSide = useCallback(
    (isLong: boolean) => {
      dispatch(selectPositionSide({ isLong }))
    },
    [dispatch]
  );

  const onSetSlippage = useCallback(
    (setSlippageValue: DefaultTxnSettings | string) => {
      if (Number(setSlippageValue) < .01 && setSlippageValue.length > 3) {
        dispatch(setSlippage({setSlippageValue: DefaultTxnSettings.DEFAULT_SLIPPAGE}))
      }
      if(setSlippageValue === '.') {
        dispatch(setSlippage({ setSlippageValue }))
      }
      if(slippageRegex.test(setSlippageValue)) {
        dispatch(setSlippage({ setSlippageValue }))
      }

      localStorage.setItem(`slippage`, setSlippageValue ?? DefaultTxnSettings.DEFAULT_SLIPPAGE)
    },
    [dispatch]
  )

  const onSetTxnDeadline = useCallback(
    (txnDeadline: DefaultTxnSettings | string) => {
      dispatch(setTxnDeadline({ txnDeadline }))
    },
    [dispatch]
  )

  const onResetBuildState = useCallback(
    () => {
      dispatch(resetBuildState())
    },
    [dispatch]
  )

  return {
    onAmountInput,
    onSelectLeverage,
    onSelectPositionSide,
    onSetSlippage,
    onSetTxnDeadline,
    onResetBuildState
  }
};

export function useDerivedBuildInfo(): {
  buildData: object | undefined
  inputError?: string
  parsedAmount?: string
}{
  const { account } = useActiveWeb3React();

  const { 
    typedValue,
    selectedLeverage,
    isLong,
    setSlippageValue,
    txnDeadline
  } = useBuildState();

  let buildData: object | undefined;

  // if any inputs missing, will not allow buildCallback to be created
  if (typedValue === '' || typedValue === '.' || isLong === null || isLong === undefined) {
    buildData = undefined;
  } else {
    buildData = {
      typedValue,
      selectedLeverage,
      isLong,
      setSlippageValue,
      txnDeadline
    }
  }

  let inputError: string | undefined;
  if (!account) {
    inputError = `Connect Wallet`
  }

  if (typedValue === '' || typedValue === '.') {
    inputError = `Input Collateral Amount`
  }

  if (!selectedLeverage) {
    inputError = `Select Leverage Amount`
  }

  if (isLong === null) {
    inputError = `Select Long or Short Position`
  }

  return {
    buildData,
    inputError,
    parsedAmount: typedValue
  }
}

// try to parse a user entered amount for a given token
export function tryParseAmount<T extends Currency>(value?: string, currency?: T): CurrencyAmount<T> | undefined {
  if (!value || !currency) {
    return undefined;
  }
  try {
    const typedValueParsed = parseUnits(value, currency.decimals).toString();
    if (typedValueParsed !== '0') {
      return CurrencyAmount.fromRawAmount(currency, JSBI.BigInt(typedValueParsed));
    }
  } catch (error) {
    // should fail if the user specifies too many decimal places of precision (or maybe exceed max uint?)
    console.debug(`Failed to parse input amount: "${value}"`, error);
  }
  // necessary for all paths to return a value
  return undefined;
}

export function useIsTxnSettingsAuto() : boolean {
  const { setSlippageValue, txnDeadline } = useAppSelector(((state) => state.build));

  if (setSlippageValue === DefaultTxnSettings.DEFAULT_SLIPPAGE && txnDeadline === DefaultTxnSettings.DEFAULT_DEADLINE) return true;
  else return false;
};

//@ts-ignore
export function useTxnSettingsManager(): [boolean, (default_slippage: DefaultTxnSettings | string, default_deadline: DefaultTxnSettings | string) => any] {
  const dispatch = useAppDispatch();
  const isAuto = useIsTxnSettingsAuto();

  const toggleSetTxnSettingsAuto = useCallback(
    (default_slippage: DefaultTxnSettings | string, default_deadline: DefaultTxnSettings | string) => {
      dispatch(setSlippage({ setSlippageValue: default_slippage }))
      dispatch(setTxnDeadline({ txnDeadline: default_deadline }))
    },
      [dispatch]
  )

  return [isAuto, toggleSetTxnSettingsAuto];
};

export function usePositionListFromSubgraph(address: string | undefined | null, marketPositionIdList: string[]) {
  const accountAddress = address ? address.toLowerCase() : undefined;  

  return usePositionListQuery(accountAddress ? { account: accountAddress, marketPositionIdList } : skipToken, { 
    refetchOnMountOrArgChange: true, 
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: false
  })
}

interface PositionMarketData {
  id: string
  feedAddress: string
}
interface PositionMarketDataV2 {
  id: string
  feedAddress: string
  isShutdown: boolean
}
export interface PositionData { 
  id: string
  positionId: string
  market: PositionMarketData
  isLong: boolean
  leverage: string
  isLiquidated: boolean
  entryPrice: string
  initialCollateral: string
  initialNotional: string
}

export interface PositionDataV2 { 
  id: string
  positionId: string
  market: PositionMarketDataV2
  initialOi: string
  initialDebt: string
  initialCollateral: string
  initialNotional: string
  leverage: string
  isLong: boolean
  entryPrice: string
  isLiquidated: boolean
  currentOi: string
  currentDebt: string
  mint: string
  createdAtTimestamp: string
  createdAtBlockNumber: string
  numberOfUniwnds: string
  builds: Build[]
  liquidates: Liquidate[]
  unwinds: Unwind[]
  fractionUnwound: string
}

export interface Build {
  collateral: string
  currentDebt: string
  currentOi: string
  id: string
  price: string
  timestamp: string
  value: string
}

export interface Liquidate {
  collateral: string
  currentDebt: string
  currentOi: string
  id: string
  isLong: boolean
  mint: string
  price: string
  timestamp: string
  value: string
}

export interface Unwind {
  collateral: string
  currentDebt: string
  currentOi: string
  id: string
  fraction: string
  isLong: boolean
  mint: string
  price: string
  timestamp: string
  value: string
  unwindNumber: string
  pnl: string
  transferAmount: string
  size: string
}

export interface UniwndsData { 
  id: string
  price: string
  value: string;
  unwindNumber: string;
  timestamp: string;
  mint: string;
  fraction: string;
  currentOi: string;
  currentDebt: string;
  collateral: string;
  position: Pick<PositionData, 'id'>
}

export function usePositionList(
  address: string | undefined | null,
  marketPositionIdList: string[]
):{  
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  error: unknown
  positions: PositionDataV2[] | undefined
  refetch: () => void
} {
  const walletPositionData = usePositionListFromSubgraph(address ? address : undefined, marketPositionIdList)
  return {
    isLoading: walletPositionData.isLoading,
    isFetching: walletPositionData.isFetching,
    isUninitialized: walletPositionData.isUninitialized,
    isError: walletPositionData.isError, 
    error: walletPositionData.error,
    positions: walletPositionData.data?.account?.positions,
    refetch: walletPositionData.refetch
  }
}

export interface PositionsTableDetails {
  numberOfLiquidatedPositions: string
  numberOfOpenPositions: string
  numberOfUnwinds: string
  realizedPnl: string
}

export function usePositionsTableDetails(
  address: string | undefined | null
):{
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  error: unknown
  positionsTableDetails: PositionsTableDetails | undefined
  refetch: () => void
} {
  const positionsTableDetails = useNumberOfPositionsFromSubgraph(address ? address : undefined)
  return {
    isLoading:  positionsTableDetails.isLoading,
    isFetching:  positionsTableDetails.isFetching,
    isUninitialized:  positionsTableDetails.isUninitialized,
    isError:  positionsTableDetails.isError, 
    error:  positionsTableDetails.error,
    positionsTableDetails: positionsTableDetails.data?.account as PositionsTableDetails | undefined,
    refetch:  positionsTableDetails.refetch
  }
}

export function useNumberOfPositionsFromSubgraph(address: string | undefined | null) {
  const accountAddress = address ? address.toLowerCase() : undefined;
  
  return useNumberOfPositionsQuery(accountAddress ? { account: accountAddress } : skipToken, {
    refetchOnMountOrArgChange: true, 
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: false
  })
}

export function isUnwindsQuery(data: OpenPositionsQuery | UnwindsQuery | LiquidatedPositionsQuery | undefined): data is UnwindsQuery {
  return typeof data === "object" && data !== undefined &&(data.account?.hasOwnProperty("unwinds") ?? false); // Check for specific property of UnwindsQuery
}

export function isLiquidatesQuery(data: OpenPositionsQuery | UnwindsQuery | LiquidatedPositionsQuery | undefined): data is LiquidatedPositionsQuery {
  return typeof data === "object" && data !== undefined && (data.account?.hasOwnProperty("liquidates") ?? false);
}

export function isOpenPositionsQuery(data: OpenPositionsQuery | UnwindsQuery | LiquidatedPositionsQuery | undefined): data is OpenPositionsQuery {
  return typeof data === "object" && data !== undefined && (data.account?.hasOwnProperty("positions") ?? false);
}

export function usePositionsTableData(
  address: string | undefined | null,
  first: number,
  skip: number,
  status: PositionStatus,
  marketId?: string | undefined,
): {
  isLoading: boolean
  isFetching: boolean
  isUninitialized: boolean
  isError: boolean
  error: unknown
  positionsTableData: OpenPositionsQuery | UnwindsQuery | LiquidatedPositionsQuery | undefined
  numberOfPositions: number
  refetch: () => void
} { 
  let query;

  if (status === PositionStatus.Open) {
    query = useOpenPositionsFromSubgraph;
  } else if (status === PositionStatus.Build) {
    query = useMarketOpenPositionsFromSubgraph;
  } else if (status === PositionStatus.Closed) {
    query = useUnwindsFromSubgraph;
  } else if (status === PositionStatus.Liquidated) {
    query = useLiquidatedPositionsFromSubgraph;
  } else {
    throw new Error(`Invalid position status: ${status}`);
  }

  let res;
  if(status === PositionStatus.Build){
    res = query(address ?? undefined, first, skip, marketId ?? undefined);
  } else{
    res = query(address ?? undefined, first, skip, undefined)
  }
  let positionsTableData:
    | OpenPositionsQuery
    | UnwindsQuery
    | LiquidatedPositionsQuery
    | MarketOpenPositionsQuery
    | undefined;

  if (status === PositionStatus.Open) {
    positionsTableData = res?.data as OpenPositionsQuery;
  } else if (status === PositionStatus.Build) {
    positionsTableData = res?.data as MarketOpenPositionsQuery;
  } else if (status === PositionStatus.Closed) {
    positionsTableData = res?.data as UnwindsQuery;
  } else if (status === PositionStatus.Liquidated) {
    positionsTableData = res?.data as LiquidatedPositionsQuery;
  }
  const hasPositions = (data: any): data is { account: { positions: any[] } } => {
    return data?.account?.positions !== undefined;
  };
  
  // Usage
  const numberOfPositions = hasPositions(positionsTableData)
  ? positionsTableData.account.positions.length
  : null!;

  return {
    isLoading: res?.isLoading,
    isFetching: res?.isFetching,
    isUninitialized: res?.isUninitialized,
    isError: res?.isError, 
    error: res?.error,
    numberOfPositions,
    positionsTableData,
    refetch: res?.refetch
  }
}

export function useOpenPositionsFromSubgraph(address: string | undefined | null, first: number, skip: number) {
  const accountAddress = address ? address.toLowerCase() : undefined;

  return useOpenPositionsQuery(accountAddress ? { account: accountAddress, first, skip } : skipToken, {
    refetchOnMountOrArgChange: true, 
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: false
  })
}

export function useMarketOpenPositionsFromSubgraph(address: string | undefined | null, first: number, skip: number, marketId: string | undefined) {
  const accountAddress = address ? address.toLowerCase() : undefined;
  
  return useMarketOpenPositionsQuery(accountAddress ? { account: accountAddress, first, skip, marketId } : skipToken, {
    refetchOnMountOrArgChange: true, 
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: false
  })
}

export function useUnwindsFromSubgraph(address: string | undefined | null, first: number, skip: number) {
  const accountAddress = address ? address.toLowerCase() : undefined;
  
  return useUnwindsQuery(accountAddress ? { account: accountAddress, first, skip } : skipToken, {
    refetchOnMountOrArgChange: true, 
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: false
  })
}

export function useLiquidatedPositionsFromSubgraph(address: string | undefined | null, first: number, skip: number) {
  const accountAddress = address ? address.toLowerCase() : undefined;
  
  return useLiquidatedPositionsQuery(accountAddress ? { account: accountAddress, first, skip } : skipToken, {
    refetchOnMountOrArgChange: true, 
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: false
  })
}
